import React from "react";
import {
  AppBar,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MenuIcon from "@mui/icons-material/Menu";
import {
  formDefinitionPathEntry,
  formResponsesPathEntry,
  useYDoc,
  viewsPathEntry,
} from "@/hooks/useY";
import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { FormResponse, FormDefinition, FormView } from "@/types/form";
import { useNavigate } from "react-router-dom";
import { downloadCSV } from "@/scripts/downloadCsv";
import { getFormResponses, getQuestions } from "@/scripts/getData";
import { Clear } from "@mui/icons-material";

const Header = () => {
  const [formResponses, setFormResponses] = useYDoc<FormResponse[]>(
    [formResponsesPathEntry],
    []
  );
  const [views, setViews] = useYDoc<FormView[]>([viewsPathEntry], []);
  const [form, setForms] = useYDoc<FormDefinition>([formDefinitionPathEntry], { sections: [] });

  const formQuestions = form.sections;
  const navigate = useNavigate();

  const questions = form.sections.flatMap((section) => section.fields);
  const responses = formResponses.map((formResponse) => formResponse.responses);

  const data = responses.map((answer) =>
    Object.fromEntries(
      questions.map((question) => [
        question.label,
        answer.find((a) => a.id === question.id)?.value,
      ])
    )
  );

  const handleCreateNewProcess = useCallback(() => {
    const newProcess: FormResponse = {
      id: uuidv4(),
      formDefinition: form,
      responses: [],
    };

    setFormResponses([...formResponses, newProcess]);
    navigate(`/form-filling?formResponseId=${newProcess.id}`);
  }, [form, navigate, formResponses, setFormResponses]);

  const isSmall = useMediaQuery("(max-width:880px)");

  const [isViewOptionOpened, setIsViewOptionOpened] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const selectView = (viewId: string) => {
    navigate(`/view-authoring?viewId=${viewId}`);
    setIsViewOptionOpened(false);
  };

  return (
    <>
      <AppBar position="static" color="transparent">
        <Toolbar>
          {isSmall ? (
            <>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setIsDrawerOpen(true)}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="left"
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
              >
                <List>
                  <ListItemButton onClick={handleCreateNewProcess}>
                    <ListItemIcon>
                      <AddIcon />
                    </ListItemIcon>
                    <ListItemText primary="New Process" />
                  </ListItemButton>
                  <ListItemButton onClick={() => navigate("/form-authoring")}>
                    <ListItemIcon>
                      <EditIcon />
                    </ListItemIcon>
                    <ListItemText primary="Edit Questions" />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => {
                      const newView: FormView = {
                        id: uuidv4(),
                        title: "",
                        content: "",
                      };
                      setViews([...views, newView]);
                      navigate(`/view-authoring?viewId=${newView.id}`);
                    }}
                  >
                    <ListItemIcon>
                      <AddIcon />
                    </ListItemIcon>
                    <ListItemText primary="New View" />
                  </ListItemButton>
                  <ListItemButton onClick={() => setIsViewOptionOpened(true)}>
                    <ListItemIcon>
                      <EditIcon />
                    </ListItemIcon>
                    <ListItemText primary="Edit View" />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => {
                      downloadCSV(data, "filledForms");
                    }}
                  >
                    <ListItemIcon>
                      <FileDownloadIcon />
                    </ListItemIcon>
                    <ListItemText primary="Download" />
                  </ListItemButton>
                </List>
              </Drawer>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="error"
                sx={{ mr: 2 }}
                startIcon={<AddIcon />}
                onClick={() => setForms({ sections: getQuestions() })}
              >
                Set Questions
              </Button>
              <Button
                variant="contained"
                color="error"
                sx={{ mr: 2 }}
                startIcon={<AddIcon />}
                onClick={() => setFormResponses(getFormResponses())}
              >
                Set Answers
              </Button>
              <Button
                variant="contained"
                color="error"
                sx={{ mr: 2 }}
                startIcon={<Clear />}
                onClick={() => setForms({ sections: [] })}
              >
                Erase Questions
              </Button>
              <Button
                variant="contained"
                color="error"
                sx={{ mr: 2 }}
                startIcon={<Clear />}
                onClick={() => setFormResponses([])}
              >
                Erase Answers
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                startIcon={<AddIcon />}
                disabled={formQuestions.length === 0}
                onClick={handleCreateNewProcess}
              >
                New Process
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                startIcon={<EditIcon />}
                onClick={() => navigate("/form-authoring")}
              >
                Edit Questions
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                startIcon={<AddIcon />}
                onClick={() => {
                  const newView: FormView = {
                    id: uuidv4(),
                    title: "",
                    content: "",
                  };
                  setViews([...views, newView]);
                  navigate(`/view-authoring?viewId=${newView.id}`);
                }}
              >
                New View
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                startIcon={<EditIcon />}
                onClick={() => setIsViewOptionOpened(true)}
              >
                Edit View
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={() => {
                  downloadCSV(data, "filledForms");
                }}
              >
                Download
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      {/* Select View */}
      <Dialog
        open={isViewOptionOpened}
        onClose={() => setIsViewOptionOpened(false)}
        aria-labelledby="section-dialog-title"
      >
        <DialogTitle>Select a view</DialogTitle>
        <DialogContent>
          <List>
            {views.map((view) => (
              <ListItemButton key={view.id} onClick={() => selectView(view.id)}>
                <ListItemText primary={view.title} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
